// src/DimensionnementClimModal.js
import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { motion } from 'framer-motion';
import { X, Zap } from 'lucide-react';

const getProductImage = (ref) => `https://devis.takoussane.com/images/${ref}.png`;

const CLIMS = [
  { btu: 9000,  cv: '0.75CV', puissance: 450,  surface: '10-15', prix: 795340,  ref: 'CLIM-9000BTU',  nom: 'Climatiseur Solaire 9000 BTU (0.75CV)',  mppt: 'MPPT-48V-20A', mpptNom: 'Régulateur MPPT 48V 20A', mpptPrix: 85000,  batteries: 4 },
  { btu: 12000, cv: '1CV',    puissance: 650,  surface: '12-25', prix: 895622,  ref: 'CLIM-12000BTU', nom: 'Climatiseur Solaire 12000 BTU (1CV)',      mppt: 'MPPT-48V-20A', mpptNom: 'Régulateur MPPT 48V 20A', mpptPrix: 85000,  batteries: 4 },
  { btu: 18000, cv: '1.5CV',  puissance: 900,  surface: '20-30', prix: 1094457, ref: 'CLIM-18000BTU', nom: 'Climatiseur Solaire 18000 BTU (1.5CV)',    mppt: 'MPPT-48V-30A', mpptNom: 'Régulateur MPPT 48V 30A', mpptPrix: 115000, batteries: 8 },
  { btu: 24000, cv: '2CV',    puissance: 1200, surface: '30-48', prix: 1391845, ref: 'CLIM-24000BTU', nom: 'Climatiseur Solaire 24000 BTU (2CV)',      mppt: 'MPPT-48V-40A', mpptNom: 'Régulateur MPPT 48V 40A', mpptPrix: 145000, batteries: 8 },
];

const CLIMS_MOBILE = [
  { btu: 9000,  cv: '0.75CV', puissance: 500, surface: '8-10',  tension: '24V', ref: 'CLIM-MOB-9K-24V',  nom: 'Climatiseur Solaire Mobile 9000 BTU 24V',  prix: 1879600, panneaux: 1, panneauW: 545, batteries: 4, batRef: 'GEL-65AH-12V',  batNom: 'Batterie 12V 65Ah',  batPrix: 85000,  controleur: 'Contrôleur Solaire 48V 20A', controleurPrix: 85000 },
  { btu: 9000,  cv: '0.75CV', puissance: 500, surface: '8-10',  tension: '48V', ref: 'CLIM-MOB-9K-48V',  nom: 'Climatiseur Solaire Mobile 9000 BTU 48V',  prix: 1994000, panneaux: 1, panneauW: 545, batteries: 4, batRef: 'GEL-65AH-12V',  batNom: 'Batterie 12V 65Ah',  batPrix: 85000,  controleur: 'Contrôleur Solaire 48V 20A', controleurPrix: 85000 },
  { btu: 12000, cv: '1CV',    puissance: 700, surface: '10-15', tension: '24V', ref: 'CLIM-MOB-12K-24V', nom: 'Climatiseur Solaire Mobile 12000 BTU 24V', prix: 2608200, panneaux: 2, panneauW: 545, batteries: 4, batRef: 'GEL-100AH-12V', batNom: 'Batterie 12V 100Ah', batPrix: 115000, controleur: 'Contrôleur Solaire 48V 20A', controleurPrix: 85000 },
  { btu: 12000, cv: '1CV',    puissance: 700, surface: '10-15', tension: '48V', ref: 'CLIM-MOB-12K-48V', nom: 'Climatiseur Solaire Mobile 12000 BTU 48V', prix: 2608200, panneaux: 2, panneauW: 545, batteries: 4, batRef: 'GEL-100AH-12V', batNom: 'Batterie 12V 100Ah', batPrix: 115000, controleur: 'Contrôleur Solaire 48V 20A', controleurPrix: 85000 },
];


const BATTERIE = { ref: 'GEL-200AH-12V', nom: 'Batterie GEL 200Ah 12V', prix: 265640 };
const CASIER   = { ref: 'CASIER-BATTERIES-48V', nom: 'Casier stockage batteries 48V', prix: 165420 };
const PANNEAU  = { ref: 'JKM545M-72HL4-BDVP', nom: 'Panneau Jinko Tiger Neo 545Wc', prix: 152220 };

const selectionnerClim = (surface, clims) => {
  if (surface <= 15) return clims[0];
  if (surface <= 25) return clims[1];
  if (surface <= 30) return clims[2];
  if (surface <= 48) return clims[3];
  return null;
};

// Mobile : surface + mode -> btu + tension auto (journée=24V, nuit/acdc=48V)
const selectionnerMobile = (surface, mode, tableauMobile) => {
  const liste = tableauMobile || CLIMS_MOBILE;
  const btu = surface <= 10 ? 9000 : 12000;
  const tension = mode === 'jour' ? '24V' : '48V';
  return liste.find(c => c.btu === btu && c.tension === tension) || liste[0];
};

const calcPanneaux = (puissance, heures) => {
  const energie = puissance * heures;
  const energiePV = energie / 0.85;
  const puissanceCrete = energiePV / 8.5;
  return Math.ceil(puissanceCrete / 545);
};

const forfaitInstallation = (btu) => {
  if (btu <= 9000)  return 87350;
  if (btu <= 12000) return 110200;
  if (btu <= 18000) return 136450;
  return 163780;
};

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));

const DimensionnementClimModal = ({ onClose, onGenererDevis }) => {
  const [etape, setEtape] = useState(1);
  const [typClim, setTypClim] = useState('split'); // 'split' | 'mobile'
  const [climsDB, setClimsDB] = useState(CLIMS);
  const [climsACDCDB, setClimsACDCDB] = useState({});
  const [installNom, setInstallNom] = useState('Installation et mise en service');
  const [supNom, setSupNom] = useState('Kit supports PV réglables (jambes)');
  const [lestageNom, setLestageNom] = useState('Dispositif lestage supports PV');
  const [climsMobileDB, setClimsMobileDB] = useState(CLIMS_MOBILE);
  const [batterieDB, setBatterieDB] = useState(BATTERIE);
  const [casierDB, setCasierDB] = useState(CASIER);
  const [panneauDB, setPanneauDB] = useState(PANNEAU);
  const [mpptDB, setMpptDB] = useState({
    'MPPT-48V-20A': { nom: 'Régulateur MPPT 48V 20A', prix: 85000 },
    'MPPT-48V-30A': { nom: 'Régulateur MPPT 48V 30A', prix: 115000 },
    'MPPT-48V-40A': { nom: 'Régulateur MPPT 48V 40A', prix: 145000 },
  });

  useEffect(() => {
    supabase.from('catalogue_produits').select('ref,nom,prix,puissance_w')
      .in('ref', ['CLIM-9000BTU','CLIM-12000BTU','CLIM-18000BTU','CLIM-24000BTU',
                  'CLIM-MOB-9K-24V','CLIM-MOB-9K-48V','CLIM-MOB-12K-24V','CLIM-MOB-12K-48V',
                  'MPPT-48V-20A','MPPT-48V-30A','MPPT-48V-40A',
                  'GEL-200AH-12V','GEL-65AH-12V','GEL-100AH-12V','JKM545M-72HL4-BDVP','CASIER-BATTERIES-48V',
                  'CLIMACDC-9000BTU','CLIMACDC-12000BTU','CLIMACDC-18000BTU','CLIMACDC-24000BTU',
                  'MPPT-1224V-20A','MPPT-1224V-30A','MPPT-1224V-40A','PRESTA-INSTALL-PV','SUP-ALU-REGL','LESTAGE-PV-BETON'])
      .eq('actif', true)
      .then(({ data }) => {
        if (!data) return;
        const map = {};
        data.forEach(p => { map[p.ref] = p; });
        const newClims = [
          { btu: 9000,  cv: '0.75CV', puissance: 450,  surface: '10-15', prix: map['CLIM-9000BTU']?.prix  || 795340,  ref: 'CLIM-9000BTU',  nom: map['CLIM-9000BTU']?.nom || 'Climatiseur Solaire 9000 BTU (0.75CV)',  mppt: 'MPPT-48V-20A', mpptNom: map['MPPT-48V-20A']?.nom || 'Régulateur MPPT 48V 20A', mpptPrix: map['MPPT-48V-20A']?.prix || 85000,  batteries: 4 },
          { btu: 12000, cv: '1CV',    puissance: 650,  surface: '12-25', prix: map['CLIM-12000BTU']?.prix || 895622,  ref: 'CLIM-12000BTU', nom: map['CLIM-12000BTU']?.nom || 'Climatiseur Solaire 12000 BTU (1CV)',      mppt: 'MPPT-48V-20A', mpptNom: map['MPPT-48V-20A']?.nom || 'Régulateur MPPT 48V 20A', mpptPrix: map['MPPT-48V-20A']?.prix || 85000,  batteries: 4 },
          { btu: 18000, cv: '1.5CV',  puissance: 900,  surface: '20-30', prix: map['CLIM-18000BTU']?.prix || 1094457, ref: 'CLIM-18000BTU', nom: map['CLIM-18000BTU']?.nom || 'Climatiseur Solaire 18000 BTU (1.5CV)',    mppt: 'MPPT-48V-30A', mpptNom: map['MPPT-48V-30A']?.nom || 'Régulateur MPPT 48V 30A', mpptPrix: map['MPPT-48V-30A']?.prix || 115000, batteries: 8 },
          { btu: 24000, cv: '2CV',    puissance: 1200, surface: '30-48', prix: map['CLIM-24000BTU']?.prix || 1391845, ref: 'CLIM-24000BTU', nom: map['CLIM-24000BTU']?.nom || 'Climatiseur Solaire 24000 BTU (2CV)',      mppt: 'MPPT-48V-40A', mpptNom: map['MPPT-48V-40A']?.nom || 'Régulateur MPPT 48V 40A', mpptPrix: map['MPPT-48V-40A']?.prix || 145000, batteries: 8 },
        ];
        setClimsDB(newClims);
        // Charger clims ACDC depuis catalogue (prix/nom distincts)  
        const acdc = {};
        ['CLIMACDC-9000BTU','CLIMACDC-12000BTU','CLIMACDC-18000BTU','CLIMACDC-24000BTU'].forEach(r => {
          if (map[r]) acdc[r] = map[r];
        });
        setClimsACDCDB(acdc);
        if (map['PRESTA-INSTALL-PV']) setInstallNom(map['PRESTA-INSTALL-PV'].nom);
        if (map['SUP-ALU-REGL']) setSupNom(map['SUP-ALU-REGL'].nom);
        if (map['LESTAGE-PV-BETON']) setLestageNom(map['LESTAGE-PV-BETON'].nom);
        // Mettre a jour mpptDB depuis catalogue (prix+nom a jour)
        const mpptMap = {};
        ['MPPT-48V-20A','MPPT-48V-30A','MPPT-48V-40A','MPPT-1224V-20A','MPPT-1224V-30A','MPPT-1224V-40A'].forEach(r => {
          if (map[r]) mpptMap[r] = { nom: map[r].nom, prix: map[r].prix };
        });
        if (Object.keys(mpptMap).length > 0) setMpptDB(prev => ({ ...prev, ...mpptMap }));
        const newClimsMobile = CLIMS_MOBILE.map(c => ({
          ...c,
          prix: map[c.ref]?.prix || c.prix,
          nom: map[c.ref]?.nom || c.nom,
          batPrix: map[c.batRef]?.prix || c.batPrix,
          batNom: map[c.batRef]?.nom || c.batNom,
          controleurPrix: map['MPPT-48V-20A']?.prix || c.controleurPrix
        }));
        setClimsMobileDB(newClimsMobile);
        if (map['GEL-200AH-12V']) setBatterieDB({ ref: 'GEL-200AH-12V', nom: map['GEL-200AH-12V'].nom, prix: map['GEL-200AH-12V'].prix });
        if (map['JKM545M-72HL4-BDVP']) setPanneauDB({ ref: 'JKM545M-72HL4-BDVP', nom: map['JKM545M-72HL4-BDVP'].nom, prix: map['JKM545M-72HL4-BDVP'].prix });
        if (map['CASIER-BATTERIES-48V']) setCasierDB({ ref: 'CASIER-BATTERIES-48V', nom: map['CASIER-BATTERIES-48V'].nom, prix: map['CASIER-BATTERIES-48V'].prix });
      });
  }, []);

  const [surface, setSurface] = useState('');
  const [showIA, setShowIA] = useState(false);
  const [promptIA, setPromptIA] = useState('');
  const [loadingIA, setLoadingIA] = useState(false);
  const [nbPieces, setNbPieces] = useState(1);
  const [heures, setHeures] = useState(8);
  const [modeAutonomie, setModeAutonomie] = useState('jour');
  const [heuresNuit, setHeuresNuit] = useState(4);
  const [resultat, setResultat] = useState(null);

  const analyserIA = async () => {
    if (!promptIA.trim()) return;
    setLoadingIA(true);
    try {
      const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer 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' },
        body: JSON.stringify({
          model: 'llama-3.3-70b-versatile',
          messages: [{
            role: 'system',
            content: 'Tu es un expert en climatisation solaire au Sénégal. Analyse la description et retourne UNIQUEMENT un JSON: {"surface": number, "nbPieces": number, "heures": number}. Surface en m², nbPieces = nombre de pièces à climatiser, heures = heures utilisation par jour.'
          }, { role: 'user', content: promptIA }],
          temperature: 0.1, max_tokens: 200
        })
      });
      const data = await res.json();
      let content = data.choices?.[0]?.message?.content || '{}';
      content = content.replace(/```json/g, '').replace(/```/g, '').trim();
      const parsed = JSON.parse(content);
      if (parsed.surface) setSurface(String(parsed.surface));
      if (parsed.nbPieces) setNbPieces(parsed.nbPieces);
      if (parsed.heures) setHeures(parsed.heures);
      setShowIA(false); setPromptIA('');
    } catch(e) { console.error('IA clim:', e); }
    setLoadingIA(false);
  };

  const calculer = () => {
    const surf = parseFloat(surface);
    if (!surf || surf <= 0) return;

    if (typClim === 'mobile') {
      // Limite surface mobile : max 15m²
      if (surf > 15) {
        alert('Les climatiseurs mobiles couvrent jusqu\'à 15m². Pour une surface plus grande, utilisez les climatiseurs Split.');
        return;
      }
      const mob = selectionnerMobile(surf, modeAutonomie, climsMobileDB);
      const installMob = 45000; // forfait installation mobile
      const composantsMob = [
        { ref: 'PRESTA-INSTALL-PV', nom: installNom, quantite: 1, prix: installMob, modifiable: true, cle: 'installation' },
      ];
      setResultat({
        mobile: true,
        clim: mob,
        surface: surf,
        nbPanneaux: mob.panneaux,
        nbUnites: 1,
        heures,
        modeAutonomie,
        batteries: modeAutonomie !== 'jour' ? [{ ref: mob.batRef, nom: mob.batNom, prix: mob.batPrix, quantite: mob.batteries }] : [],
        composants: composantsMob,
        installation: installMob,
      });
      setEtape(3);
      return;
    }

    // Split classique
    let clim = selectionnerClim(surf, climsDB);
    if (!clim) {
      const clim24 = CLIMS[3];
      const nb = Math.ceil(surf / 48);
      const installMulti = forfaitInstallation(24000);
      const cableDC6m = ((calcPanneaux(clim24.puissance, heures) * nb * 2) + 20) * 2;
      const composantsMulti = [
        { ref: 'ACC-CABLE-SOL-DC-25MM', nom: 'Câble solaire DC 25mm',       quantite: 10,         unite: 'm', prix: 3380,  modifiable: true,  cle: 'cableDC25' },
        { ref: 'ACC-CABLE-DC-6MM2',     nom: 'Câble DC 6mm',                quantite: cableDC6m,  unite: 'm', prix: 1710,  modifiable: true,  cle: 'cableDC6' },
        { ref: 'ACC-CABLE-AC-2X6MM',    nom: 'Câble électrique AC 2x6mm',   quantite: 15 * nb,    unite: 'm', prix: 1850,  modifiable: true,  cle: 'cableAC6' },
        { ref: 'ACC-CABLE-HO7RNF-25MM', nom: 'Câble HO7RN-F 2.5mm',         quantite: cableDC6m,  unite: 'm', prix: 414,   modifiable: true,  cle: 'cableHO7' },
        { ref: 'MC4',                    nom: 'Connecteur MC4 Male/Femelle',  quantite: 2 * nb,                prix: 2230,  modifiable: true,  cle: 'mc4' },
        { ref: 'GOULOTTE-40x20',         nom: 'Goulotte PVC 40x20mm (L3m)', quantite: 2 * nb,                prix: 12300, modifiable: true,  cle: 'goulotte1' },
        { ref: 'GOULOTTE-80x40',         nom: 'Goulotte PVC 80x40mm (L3m)', quantite: nb,                    prix: 13950, modifiable: true,  cle: 'goulotte2' },
        { ref: 'COLLIERS-PLASTIQUES',    nom: 'Lot colliers attachés',       quantite: nb,                    prix: 9250,  modifiable: false },
        { ref: 'KIT-CHEVILLES-VIS',      nom: 'Kit chevilles et vis n8',     quantite: nb,                    prix: 2150,  modifiable: false },
        { ref: 'TUBE-IRL-25',            nom: 'Tube IRL conduit rigide L10m',quantite: nb,                    prix: 14400, modifiable: true,  cle: 'tube' },
        { ref: 'DISJONCTEUR-DIFF-2P40',  nom: 'Disjoncteur Schneider 2P40', quantite: nb,                    prix: 30840, modifiable: false },
        { ref: 'BOITE-DERIVATION-IP65',  nom: 'Boîtes dérivation IP65 x2',  quantite: nb,                    prix: 2200,  modifiable: false },
        { ref: 'LESTAGE-PV-BETON',       nom: lestageNom,    quantite: calcPanneaux(clim24.puissance, heures) * nb * 3, prix: 6410,  modifiable: true,  cle: 'lestage' },
        { ref: 'SUP-ALU-REGL',           nom: supNom, quantite: calcPanneaux(clim24.puissance, heures) * nb,              prix: 25120, modifiable: true,  cle: 'supports' },
        { ref: 'PRESTA-INSTALL-PV',      nom: installNom, quantite: 1,                 prix: installMulti, modifiable: true, cle: 'installation' },
      ];
      setResultat({ multiUnit: true, nb, clim: clim24, surf, nbPanneaux: calcPanneaux(clim24.puissance, heures) * nb, nbUnites: nb, heures, modeAutonomie, heuresNuit, batteries: [], casier: false, composants: composantsMulti, installation: installMulti });
      setEtape(3);
      return;
    }

    const nbPanneaux = calcPanneaux(clim.puissance, heures);
    const installation = forfaitInstallation(clim.btu);
    const cableDC6 = ((nbPanneaux * 2) + 20) * 2;
    const cableAC6 = 15;
    const cableHO7 = cableDC6;
    const cableDC25 = 10;

    const composants = [
      { ref: 'ACC-CABLE-SOL-DC-25MM', nom: 'Câble solaire DC 25mm',       quantite: cableDC25, unite: 'm', prix: 3380,  modifiable: true,  cle: 'cableDC25' },
      { ref: 'ACC-CABLE-DC-6MM2',     nom: 'Câble DC 6mm',                quantite: cableDC6,  unite: 'm', prix: 1710,  modifiable: true,  cle: 'cableDC6' },
      { ref: 'ACC-CABLE-AC-2X6MM',    nom: 'Câble électrique AC 2x6mm',   quantite: cableAC6,  unite: 'm', prix: 1850,  modifiable: true,  cle: 'cableAC6' },
      { ref: 'ACC-CABLE-HO7RNF-25MM', nom: 'Câble HO7RN-F 2.5mm',         quantite: cableHO7,  unite: 'm', prix: 414,   modifiable: true,  cle: 'cableHO7' },
      { ref: 'MC4',                    nom: 'Connecteur MC4 Male/Femelle',     quantite: 2,                     prix: 2230,  modifiable: true,  cle: 'mc4' },
      { ref: 'GOULOTTE-40x20',         nom: 'Goulotte PVC 40x20mm (L3m)',  quantite: 2,                     prix: 12300, modifiable: true,  cle: 'goulotte1' },
      { ref: 'GOULOTTE-80x40',         nom: 'Goulotte PVC 80x40mm (L3m)',  quantite: 1,                     prix: 13950, modifiable: true,  cle: 'goulotte2' },
      { ref: 'COLLIERS-PLASTIQUES',    nom: 'Lot colliers attachés',        quantite: 1,                     prix: 9250,  modifiable: false },
      { ref: 'KIT-CHEVILLES-VIS',      nom: 'Kit chevilles et vis n8',      quantite: 1,                     prix: 2150,  modifiable: false },
      { ref: 'TUBE-IRL-25',            nom: 'Tube IRL conduit rigide L10m', quantite: 1,                     prix: 14400, modifiable: true,  cle: 'tube' },
      { ref: 'DISJONCTEUR-DIFF-2P40',  nom: 'Disjoncteur Schneider 2P40',  quantite: 1,                     prix: 30840, modifiable: false },
      { ref: 'BOITE-DERIVATION-IP65',  nom: 'Boîtes dérivation IP65 x2',   quantite: 1,                     prix: 2200,  modifiable: false },
      { ref: 'LESTAGE-PV-BETON',    nom: lestageNom,    quantite: nbPanneaux * 3, prix: 6410,  modifiable: true,  cle: 'lestage' },
      { ref: 'SUP-ALU-REGL',        nom: supNom, quantite: nbPanneaux,              prix: 25120, modifiable: true,  cle: 'supports' },
      { ref: 'PRESTA-INSTALL-PV',      nom: installNom, quantite: 1,                  prix: installation, modifiable: true, cle: 'installation' },
    ];

    let batteries = [];
    let casier = false;
    if (modeAutonomie === 'nuit') {
      batteries = [{ ...batterieDB, quantite: clim.batteries }];
      casier = true;
    }
    if (modeAutonomie === 'acdc') {
      batteries = [];
      casier = false;
      // Substituer par la clim ACDC correspondante (prix/nom distincts)
      const refACDC = 'CLIMACDC-' + clim.btu + 'BTU';
      if (climsACDCDB[refACDC]) {
        clim = { ...clim, ref: refACDC, nom: climsACDCDB[refACDC].nom || clim.nom, prix: climsACDCDB[refACDC].prix ?? clim.prix };
      }
    }

    setResultat({ clim, nbPanneaux: nbPanneaux * nbPieces, nbUnites: nbPieces, heures, modeAutonomie, heuresNuit, batteries, casier, composants, installation });
    setEtape(3);
  };

  const updateComposant = (cle, newQty) => {
    setResultat(prev => ({ ...prev, composants: prev.composants.map(c => c.cle === cle ? { ...c, quantite: Math.max(0, newQty) } : c) }));
  };

  const updatePrix = (cle, newPrix) => {
    setResultat(prev => ({ ...prev, composants: prev.composants.map(c => c.cle === cle ? { ...c, prix: Math.max(0, newPrix) } : c) }));
  };

  const totalResultat = resultat ? (
    resultat.mobile
      ? (resultat.clim.prix) +
        (resultat.clim.controleurPrix) +
        (panneauDB.prix * resultat.nbPanneaux) +
        resultat.batteries.reduce((s, b) => s + b.quantite * b.prix, 0) +
        resultat.composants.reduce((s, c) => s + (c.quantite * c.prix), 0)
      : (resultat.clim.prix * (resultat.nbUnites || 1)) +
        (panneauDB.prix * resultat.nbPanneaux) +
        (resultat.clim.mpptPrix * (resultat.nbUnites || 1)) +
        resultat.batteries.reduce((s, b) => s + b.quantite * b.prix, 0) +
        (resultat.casier ? casierDB.prix : 0) +
        resultat.composants.reduce((s, c) => s + (c.quantite * c.prix), 0)
  ) : 0;

  const genererDevis = () => {
    if (!resultat) return;
    const articles = [];
    let uid = 0;

    if (resultat.mobile) {
      const mob = resultat.clim;
      const modeLabel = modeAutonomie === 'jour' ? 'Clim Mobile Solaire DC 24V (Journée)' : 'Clim Mobile Solaire DC 48V (Jour + Nuit)';
      articles.push({
        _uid: uid++, _titre: true,
        description: 'KIT CLIMATISATION SOLAIRE MOBILE TAKOUSSANE ENERGY  --  ' + mob.btu + ' BTU (' + mob.cv + ')  |  Surface: ' + surface + 'm2  |  Mode: ' + modeLabel + '  |  Tension: ' + mob.tension,
        quantity: null, unitPrice: null, image: null,
      });
      articles.push({
        _uid: uid++,
        description: (climsMobileDB.find(c => c.ref === mob.ref)?.nom || mob.nom) + '\nRef: ' + mob.ref + '\nPortable DC ' + mob.tension + ' - Autonomie: 8h/jour + 4h/nuit',
        quantity: 1, unitPrice: mob.prix, image: getProductImage(mob.ref),
      });
      articles.push({
        _uid: uid++,
        description: 'Panneau Solaire ' + mob.panneauW + 'Wc\nRef: ' + panneauDB.ref,
        quantity: mob.panneaux, unitPrice: panneauDB.prix, image: getProductImage(panneauDB.ref),
      });
      articles.push({
        _uid: uid++,
        description: (mpptDB['MPPT-48V-20A']?.nom || mob.controleur) + '\nRef: MPPT-48V-20A',
        quantity: 1, unitPrice: mob.controleurPrix, image: getProductImage('MPPT-48V-20A'),
      });
      resultat.batteries.forEach(function(b) {
        articles.push({ _uid: uid++, description: b.nom + '\nRef: ' + b.ref, quantity: b.quantite, unitPrice: b.prix, image: getProductImage(b.ref) });
      });
      resultat.composants.forEach(function(c) {
        if (c.quantite > 0) articles.push({ _uid: uid++, description: c.nom, quantity: c.quantite, unitPrice: c.prix, image: getProductImage(c.ref) });
      });
    } else {
      var nbPiecesLabel = resultat.nbUnites > 1 ? resultat.nbUnites + ' pièces' : '1 pièce';
      var modeLabel = resultat.modeAutonomie === 'nuit' ? 'Clim Solaire DC + Batteries (24h)' : resultat.modeAutonomie === 'acdc' ? 'Clim Solaire ACDC On-Grid (Jour+Nuit)' : 'Clim Solaire DC (Journée)';
      articles.push({
        _uid: uid++, _titre: true,
        description: 'KIT CLIMATISATION SOLAIRE TAKOUSSANE ENERGY  --  ' + resultat.clim.btu + ' BTU (' + resultat.clim.cv + ')  |  Surface: ' + surface + 'm2  |  ' + nbPiecesLabel + '  |  Mode: ' + modeLabel,
        quantity: null, unitPrice: null, image: null,
      });
      articles.push({
        description: (climsDB.find(c => c.ref === resultat.clim.ref)?.nom || resultat.clim.nom) + '\nRef: ' + resultat.clim.ref + (resultat.modeAutonomie === 'acdc' ? '\nMode ACDC On-Grid - Bascule solaire/SENELEC automatique' : '\nMode Split DC 48V - Autonomie solaire'),
        quantity: resultat.nbUnites, unitPrice: resultat.clim.prix, image: getProductImage(resultat.modeAutonomie === 'acdc' ? 'CLIMACDC-' + resultat.clim.btu + 'BTU' : resultat.clim.ref),
      });
      articles.push({ _uid: uid++, description: panneauDB.nom + '\nRef: ' + panneauDB.ref, quantity: resultat.nbPanneaux, unitPrice: panneauDB.prix, image: getProductImage(panneauDB.ref) });
      if (resultat.modeAutonomie !== 'acdc') articles.push({ _uid: uid++, description: (mpptDB[resultat.clim.mppt]?.nom || resultat.clim.mpptNom) + '\nRef: ' + resultat.clim.mppt, quantity: resultat.nbUnites, unitPrice: mpptDB[resultat.clim.mppt]?.prix ?? resultat.clim.mpptPrix, image: getProductImage(resultat.clim.mppt) });
      if (resultat.modeAutonomie === 'nuit') {
        resultat.batteries.forEach(function(b) { articles.push({ _uid: uid++, description: b.nom + '\nRef: ' + b.ref, quantity: b.quantite, unitPrice: b.prix, image: getProductImage(b.ref) }); });
        if (resultat.casier) articles.push({ _uid: uid++, description: casierDB.nom + '\nRef: ' + casierDB.ref, quantity: 1, unitPrice: CASIER.prix, image: getProductImage(CASIER.ref) });
      }
      resultat.composants.forEach(function(c) {
        if (c.quantite > 0) articles.push({ _uid: uid++, description: c.nom + (c.unite ? ' (' + c.quantite + c.unite + ')' : ''), quantity: c.unite ? 1 : c.quantite, unitPrice: c.unite ? c.quantite * c.prix : c.prix, image: getProductImage(c.ref) });
      });
    }

    onGenererDevis(articles);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 70, padding: '16px' }}
      onClick={function(e) { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '860px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
      >
        <div style={{ background: 'linear-gradient(135deg, #0ea5e9, #0284c7)', padding: '20px 24px', borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: 'white', fontWeight: 700, fontSize: '18px' }}>Dimensionnement Climatisation Solaire</div>
            <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px', marginTop: '2px' }}>
              {etape === 1 ? 'Étape 1 - Type et données' : etape === 2 ? 'Étape 2 - Mode' : 'Étape 3 - Kit dimensionné'}
            </div>
          </div>
          <button onClick={() => setShowIA(!showIA)} style={{ background: showIA ? 'linear-gradient(135deg,#7c3aed,#a855f7)' : 'white', border: 'none', borderRadius: '8px', padding: '6px 14px', color: showIA ? 'white' : '#7c3aed', fontSize: '12px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>✨ IA</button>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {[1,2,3].map(function(n) {
              return (<div key={n} style={{ width: '28px', height: '28px', borderRadius: '50%', background: etape >= n ? 'white' : 'rgba(255,255,255,0.25)', color: etape >= n ? '#0284c7' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>{n}</div>);
            })}
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', borderRadius: '6px', padding: '6px', marginLeft: '8px', display: 'flex' }}><X size={20} /></button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {etape === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Toggle Split / Mobile */}
              <div>
                <label style={{ fontWeight: 700, fontSize: '14px', color: '#1f2937', display: 'block', marginBottom: '10px' }}>Type de climatiseur</label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button onClick={() => setTypClim('split')}
                    style={{ flex: 1, padding: '16px', textAlign: 'left', border: '2px solid ' + (typClim === 'split' ? '#0284c7' : '#e5e7eb'), borderRadius: '10px', background: typClim === 'split' ? '#f0f9ff' : 'white', cursor: 'pointer', transition: 'all 0.15s' }}>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: typClim === 'split' ? '#0284c7' : '#374151' }}>🏠 Climatiseur Solaire Split</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Split mural fixe — 9000 à 24000 BTU — jusqu'à 48m²</div>
                  </button>
                  <button onClick={() => setTypClim('mobile')}
                    style={{ flex: 1, padding: '16px', textAlign: 'left', border: '2px solid ' + (typClim === 'mobile' ? '#f59e0b' : '#e5e7eb'), borderRadius: '10px', background: typClim === 'mobile' ? '#fffbeb' : 'white', cursor: 'pointer', transition: 'all 0.15s' }}>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: typClim === 'mobile' ? '#d97706' : '#374151' }}>🧳 Climatiseur Solaire Mobile</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Portable — 9000 ou 12000 BTU — 8 à 15m² — sans installation murale</div>
                  </button>
                </div>
              </div>

              {typClim === 'mobile' && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '12px 14px', fontSize: '13px', color: '#92400e' }}>
                  ✅ Tension sélectionnée automatiquement : <strong>24V</strong> (mode journée) ou <strong>48V</strong> (mode nuit/on-grid). Aucun choix manuel nécessaire.
                </div>
              )}

              <div style={{ background: '#f0f9ff', borderRadius: '10px', padding: '14px', border: '1px solid #bae6fd', fontSize: '13px', color: '#0369a1' }}>
                {typClim === 'mobile'
                  ? 'Renseignez la surface de la pièce — le modèle adapté (9000 ou 12000 BTU) sera sélectionné automatiquement. Surface max : 15m².'
                  : 'Renseignez la surface de la pièce — le climatiseur adapté sera sélectionné automatiquement.'}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: typClim === 'split' ? '1fr 1fr' : '1fr', gap: '16px' }}>
                <div>
                  {showIA && (
                    <div style={{ background: 'linear-gradient(135deg,#f5f3ff,#ede9fe)', border: '1px solid #c4b5fd', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: '#5b21b6', marginBottom: '4px' }}>✨ Décris le projet, l'IA s'occupe du reste</div>
                      <div style={{ fontSize: '11px', color: '#7c3aed', marginBottom: '10px' }}>Ex: salon 25m² + 2 chambres 15m² chacune, utilisation 8h/j à Dakar</div>
                      <textarea value={promptIA} onChange={e => setPromptIA(e.target.value)} rows={2}
                        placeholder="Décris le projet de climatisation..."
                        style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #c4b5fd', borderRadius: '8px', padding: '8px', fontSize: '13px', outline: 'none', background: 'white', resize: 'none', fontFamily: 'inherit' }} />
                      <button onClick={analyserIA} disabled={loadingIA || !promptIA.trim()}
                        style={{ marginTop: '8px', width: '100%', padding: '9px', background: loadingIA ? '#a78bfa' : 'linear-gradient(135deg,#7c3aed,#a855f7)', border: 'none', borderRadius: '8px', color: 'white', fontSize: '13px', fontWeight: 700, cursor: loadingIA ? 'wait' : 'pointer' }}>
                        {loadingIA ? '⏳ Analyse...' : '✨ Analyser'}
                      </button>
                    </div>
                  )}
                  <label style={{ fontWeight: 700, fontSize: '14px', color: '#1f2937', display: 'block', marginBottom: '8px' }}>Surface de la pièce (m²) *</label>
                  <input type="number" value={surface} min="1" max={typClim === 'mobile' ? 15 : 200}
                    onChange={function(e) { setSurface(e.target.value); }}
                    placeholder={typClim === 'mobile' ? 'Ex: 12 (max 15m²)' : 'Ex: 20'}
                    autoFocus
                    style={{ width: '100%', boxSizing: 'border-box', border: '2px solid #e5e7eb', borderRadius: '8px', padding: '10px 12px', fontSize: '16px', outline: 'none', fontWeight: 600 }}
                    onFocus={function(e) { e.target.style.borderColor='#0284c7'; }}
                    onBlur={function(e) { e.target.style.borderColor='#e5e7eb'; }}
                  />
                </div>
                {typClim === 'split' && (
                  <div>
                    <label style={{ fontWeight: 700, fontSize: '14px', color: '#1f2937', display: 'block', marginBottom: '8px' }}>Nombre de pièces</label>
                    <input type="number" value={nbPieces} min="1"
                      onChange={function(e) { setNbPieces(parseInt(e.target.value) || 1); }}
                      style={{ width: '100%', boxSizing: 'border-box', border: '2px solid #e5e7eb', borderRadius: '8px', padding: '10px 12px', fontSize: '16px', outline: 'none' }}
                      onFocus={function(e) { e.target.style.borderColor='#0284c7'; }}
                      onBlur={function(e) { e.target.style.borderColor='#e5e7eb'; }}
                    />
                  </div>
                )}
                <div>
                  <label style={{ fontWeight: 700, fontSize: '14px', color: '#1f2937', display: 'block', marginBottom: '8px' }}>Heures utilisation/jour</label>
                  <input type="number" value={heures} min="1" max="24" step="0.5"
                    onChange={function(e) { setHeures(parseFloat(e.target.value) || 8); }}
                    style={{ width: '100%', boxSizing: 'border-box', border: '2px solid #e5e7eb', borderRadius: '8px', padding: '10px 12px', fontSize: '16px', outline: 'none' }}
                    onFocus={function(e) { e.target.style.borderColor='#0284c7'; }}
                    onBlur={function(e) { e.target.style.borderColor='#e5e7eb'; }}
                  />
                </div>
              </div>

              {surface > 0 && (
                <div style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '14px' }}>
                  {typClim === 'mobile' ? (
                    parseFloat(surface) > 15 ? (
                      <div style={{ color: '#ef4444', fontWeight: 600 }}>⚠️ Surface trop grande pour le mobile (max 15m²) — utilisez le mode Split</div>
                    ) : (
                      <div style={{ color: '#065f46' }}>
                        <span style={{ fontWeight: 700 }}>Modèle recommandé : </span>
                        {parseFloat(surface) <= 10 ? 'Climatiseur Solaire Mobile 9000 BTU' : 'Climatiseur Solaire Mobile 12000 BTU'}
                        {' — '}<span style={{ color: '#d97706', fontWeight: 600 }}>Tension 24V (journée) ou 48V (nuit) — sélectionnée à l'étape suivante</span>
                      </div>
                    )
                  ) : (
                    (function() {
                      var clim = selectionnerClim(parseFloat(surface), climsDB);
                      if (!clim) return <div style={{ color: '#065f46', fontWeight: 600 }}>Surface supérieure à 48m2 — {Math.ceil(parseFloat(surface)/48)} unités 24000 BTU recommandées</div>;
                      return <div style={{ color: '#065f46' }}><span style={{ fontWeight: 700 }}>Climatiseur recommandé : </span>{clim.nom} — Surface adaptée : {clim.surface}m²</div>;
                    })()
                  )}
                </div>
              )}
            </div>
          )}

          {etape === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{ fontWeight: 700, fontSize: '14px', color: '#1f2937', display: 'block', marginBottom: '10px' }}>Mode d'alimentation</label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button onClick={function() { setModeAutonomie('jour'); }}
                    style={{ flex: 1, padding: '16px', textAlign: 'left', border: '2px solid ' + (modeAutonomie === 'jour' ? '#0284c7' : '#e5e7eb'), borderRadius: '10px', background: modeAutonomie === 'jour' ? '#f0f9ff' : 'white', cursor: 'pointer', transition: 'all 0.15s' }}>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: modeAutonomie === 'jour' ? '#0284c7' : '#374151' }}>
                      {typClim === 'mobile' ? '☀️ Solaire DC 24V (Journée)' : 'Clim Solaire DC (Journée)'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      {typClim === 'mobile' ? 'Fonctionne avec le soleil — tension 24V — kit inclus dans le prix' : 'Fonctionne uniquement avec le soleil — sans batteries — économique'}
                    </div>
                  </button>
                  {typClim === 'split' && (
                    <button onClick={function() { setModeAutonomie('acdc'); }}
                      style={{ flex: 1, padding: '16px', textAlign: 'left', border: '2px solid ' + (modeAutonomie === 'acdc' ? '#7c3aed' : '#e5e7eb'), borderRadius: '10px', background: modeAutonomie === 'acdc' ? '#f5f3ff' : 'white', cursor: 'pointer', transition: 'all 0.15s' }}>
                      <div style={{ fontWeight: 700, fontSize: '14px', color: modeAutonomie === 'acdc' ? '#7c3aed' : '#374151' }}>⚡ Clim Solaire ACDC (On-Grid)</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Solaire le jour, bascule SENELEC/groupe le soir — sans batteries</div>
                    </button>
                  )}
                  <button onClick={function() { setModeAutonomie('nuit'); }}
                    style={{ flex: 1, padding: '16px', textAlign: 'left', border: '2px solid ' + (modeAutonomie === 'nuit' ? '#0284c7' : '#e5e7eb'), borderRadius: '10px', background: modeAutonomie === 'nuit' ? '#f0f9ff' : 'white', cursor: 'pointer', transition: 'all 0.15s' }}>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: modeAutonomie === 'nuit' ? '#0284c7' : '#374151' }}>
                      {typClim === 'mobile' ? '🔋 Solaire DC 48V (Jour + Nuit)' : 'Clim Solaire DC + Batteries (24h)'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      {typClim === 'mobile' ? 'Batteries incluses — tension 48V — indépendance totale' : 'Fonctionne jour ET nuit — indépendance totale de la Senelec'}
                    </div>
                  </button>
                </div>
              </div>

              {typClim === 'mobile' && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '12px 14px', fontSize: '13px', color: '#92400e' }}>
                  <strong>Tension sélectionnée automatiquement :</strong> {modeAutonomie === 'jour' ? '24V DC (journée solaire)' : '48V DC (autonomie nuit avec batteries incluses dans le kit)'}
                </div>
              )}

              {modeAutonomie === 'nuit' && typClim === 'split' && (
                <div>
                  <label style={{ fontWeight: 700, fontSize: '14px', color: '#1f2937', display: 'block', marginBottom: '8px' }}>Heures d'utilisation la nuit</label>
                  <input type="number" value={heuresNuit} min="1" max="12" step="0.5"
                    onChange={function(e) { setHeuresNuit(parseFloat(e.target.value) || 4); }}
                    style={{ width: '120px', border: '2px solid #e5e7eb', borderRadius: '8px', padding: '10px 12px', fontSize: '16px', outline: 'none' }}
                    onFocus={function(e) { e.target.style.borderColor='#0284c7'; }}
                    onBlur={function(e) { e.target.style.borderColor='#e5e7eb'; }}
                  />
                </div>
              )}
            </div>
          )}

          {etape === 3 && resultat && (
            <div>
              <div style={{ background: resultat.mobile ? 'linear-gradient(135deg, #fffbeb, #fef3c7)' : 'linear-gradient(135deg, #ecfdf5, #d1fae5)', border: resultat.mobile ? '1px solid #fde68a' : '1px solid #6ee7b7', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: resultat.mobile ? '#92400e' : '#065f46', marginBottom: '10px' }}>
                  {resultat.mobile ? '🧳 KIT CLIMATISATION MOBILE' : 'KIT CLIMATISATION SPLIT'} — {resultat.clim.btu} BTU ({resultat.clim.cv}){resultat.mobile ? ' — ' + resultat.clim.tension : ''}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  {[
                    { label: 'Surface', value: surface + ' m²' },
                    { label: 'Panneaux 545Wc', value: resultat.nbPanneaux + ' panneaux' },
                    { label: 'Mode', value: resultat.mobile ? (modeAutonomie === 'jour' ? 'Journée 24V' : 'Jour+Nuit 48V') : (resultat.modeAutonomie === 'nuit' ? 'DC+Batteries 24h' : resultat.modeAutonomie === 'acdc' ? 'ACDC On-Grid' : 'DC Journée') },
                  ].map(function(item) {
                    return (
                      <div key={item.label} style={{ background: 'white', borderRadius: '8px', padding: '10px 12px', border: resultat.mobile ? '1px solid #fde68a' : '1px solid #a7f3d0' }}>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>{item.label}</div>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: resultat.mobile ? '#d97706' : '#065f46' }}>{item.value}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', padding: '0 4px' }}>
                Composition du kit
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {/* Clim */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 110px', gap: '8px', alignItems: 'center', background: '#e0f2fe', borderRadius: '8px', padding: '10px 12px', border: '1px solid #7dd3fc' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>{resultat.clim.nom}</div>
                  <div style={{ textAlign: 'center', fontSize: '14px', fontWeight: 700 }}>1</div>
                  <div style={{ textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>{fmt(resultat.clim.prix)} F</div>
                  <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 700, color: '#0284c7' }}>{fmt(resultat.clim.prix)} F</div>
                </div>
                {/* Panneaux */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 110px', gap: '8px', alignItems: 'center', background: '#fef3c7', borderRadius: '8px', padding: '10px 12px', border: '1px solid #fde68a' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>{panneauDB.nom}</div>
                  <div style={{ textAlign: 'center', fontSize: '14px', fontWeight: 700 }}>{resultat.nbPanneaux}</div>
                  <div style={{ textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>{fmt(panneauDB.prix)} F</div>
                  <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 700, color: '#0284c7' }}>{fmt(panneauDB.prix * resultat.nbPanneaux)} F</div>
                </div>
                {/* Contrôleur / MPPT - masque en mode ACDC */}
                {resultat.modeAutonomie !== 'acdc' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 110px', gap: '8px', alignItems: 'center', background: '#f0f9ff', borderRadius: '8px', padding: '10px 12px', border: '1px solid #bae6fd' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>{resultat.mobile ? resultat.clim.controleur : resultat.clim.mpptNom}</div>
                  <div style={{ textAlign: 'center', fontSize: '14px', fontWeight: 700 }}>1</div>
                  <div style={{ textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>{fmt(resultat.mobile ? resultat.clim.controleurPrix : resultat.clim.mpptPrix)} F</div>
                  <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 700, color: '#0284c7' }}>{fmt(resultat.mobile ? resultat.clim.controleurPrix : resultat.clim.mpptPrix)} F</div>
                </div>
                )}
                {/* Batteries */}
                {resultat.batteries.map(function(b, i) {
                  return (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 110px', gap: '8px', alignItems: 'center', background: '#f0fdf4', borderRadius: '8px', padding: '10px 12px', border: '1px solid #bbf7d0' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>{b.nom}</div>
                      <div style={{ textAlign: 'center', fontSize: '14px', fontWeight: 700 }}>{b.quantite}</div>
                      <div style={{ textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>{fmt(b.prix)} F</div>
                      <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 700, color: '#0284c7' }}>{fmt(b.quantite * b.prix)} F</div>
                    </div>
                  );
                })}
                {!resultat.mobile && resultat.casier && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 110px', gap: '8px', alignItems: 'center', background: '#f8fafc', borderRadius: '8px', padding: '8px 12px', border: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: '12px', color: '#374151' }}>{casierDB.nom}</div>
                    <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: 600 }}>1</div>
                    <div style={{ textAlign: 'right', fontSize: '12px', color: '#6b7280' }}>{fmt(casierDB.prix)} F</div>
                    <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 600, color: '#0284c7' }}>{fmt(CASIER.prix)} F</div>
                  </div>
                )}
                {/* Composants */}
                {resultat.composants.map(function(c, i) {
                  return (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 110px', gap: '8px', alignItems: 'center', background: i % 2 === 0 ? '#f8fafc' : 'white', borderRadius: '8px', padding: '8px 12px', border: '1px solid #f3f4f6' }}>
                      <div style={{ fontSize: '12px', color: '#374151' }}>{c.nom}</div>
                      {c.modifiable ? (
                        <input type="number" value={c.quantite} min="0" onChange={function(e) { updateComposant(c.cle, parseFloat(e.target.value) || 0); }} style={{ border: '1px solid #e5e7eb', borderRadius: '4px', padding: '4px', fontSize: '13px', textAlign: 'center', width: '100%', outline: 'none' }} />
                      ) : (
                        <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: 600 }}>{c.quantite}</div>
                      )}
                      {c.modifiable ? (
                        <input type="number" value={c.prix} min="0" onChange={function(e) { updatePrix(c.cle, parseFloat(e.target.value) || 0); }} style={{ border: '1px solid #e5e7eb', borderRadius: '4px', padding: '4px', fontSize: '12px', textAlign: 'right', width: '100%', outline: 'none' }} />
                      ) : (
                        <div style={{ textAlign: 'right', fontSize: '12px', color: '#6b7280' }}>{fmt(c.prix)} F</div>
                      )}
                      <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: 600, color: '#0284c7' }}>{fmt(c.quantite * c.prix)} F</div>
                    </div>
                  );
                })}
              </div>

              <div style={{ marginTop: '16px', background: '#0284c7', borderRadius: '10px', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Total kit estimé</span>
                <span style={{ color: 'white', fontWeight: 800, fontSize: '20px' }}>{fmt(totalResultat)} FCFA</span>
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderRadius: '0 0 16px 16px' }}>
          <div>
            {etape > 1 && (
              <button onClick={function() { setEtape(etape - 1); }} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 18px', fontSize: '13px', cursor: 'pointer', fontWeight: 500 }}>Retour</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onClose} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 18px', fontSize: '13px', cursor: 'pointer', fontWeight: 500 }}>Fermer</button>
            {etape === 1 && (
              <button
                onClick={function() {
                  if (!surface || parseFloat(surface) <= 0) return;
                  if (typClim === 'mobile' && parseFloat(surface) > 15) return;
                  setEtape(2);
                }}
                disabled={!surface || parseFloat(surface) <= 0 || (typClim === 'mobile' && parseFloat(surface) > 15)}
                style={{ background: (surface > 0 && !(typClim === 'mobile' && parseFloat(surface) > 15)) ? '#0284c7' : '#e5e7eb', color: (surface > 0 && !(typClim === 'mobile' && parseFloat(surface) > 15)) ? 'white' : '#9ca3af', border: 'none', borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                Suivant
              </button>
            )}
            {etape === 2 && (
              <button onClick={calculer} style={{ background: '#0284c7', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Zap size={14} /> Dimensionner
              </button>
            )}
            {etape === 3 && (
              <button onClick={genererDevis} style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                Générer le devis
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DimensionnementClimModal;
